import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalContext } from '~/context/GlobalContext';
import { apiService } from '~/services/apiService';
import adminStyles from '~/styles/AdminPanel/AdminPanel.module.css';
import styles from '~/styles/AdminPanel/Users.module.css';

type UserRole = 'administrator' | 'moderator' | 'user';
type UserGender = 'male' | 'female';
type RoleFilter = 'all' | UserRole;
type StatusFilter = 'all' | 'activated' | 'notActivated' | 'banned';
type SourceFilter = 'all' | 'registered' | 'guest' | 'ownNick';
type ItemSource = 'registered' | 'guest' | 'ownNick';

interface IFilters {
  role: RoleFilter;
  status: StatusFilter;
  source: SourceFilter;
  search: string;
}

interface IDbUser {
  id: string;
  nick: string;
  email: string;
  role: UserRole;
  gender: UserGender;
  isBanned: boolean;
  isActivated: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface IOnlineSession {
  id: string;
  nick: string;
  gender: UserGender;
  deviceType: string | null;
  ipAddress: string | null;
  status: string;
  time: string | null;
  role: UserRole;
  source: 'guest' | 'ownNick' | 'registered';
  isRegistered: boolean;
}

interface IListItem {
  key: string;
  source: ItemSource;
  id: string;
  nick: string;
  email?: string;
  role: UserRole;
  gender: UserGender;
  isBanned?: boolean;
  isActivated?: boolean;
  isOnline: boolean;
  ipAddress?: string | null;
  deviceType?: string | null;
  status?: string;
  // referencja do oryginalnego rekordu z DB (potrzebna do edycji)
  dbUser?: IDbUser;
}

interface IEditState {
  nick: string;
  email: string;
  password: string;
  role: UserRole;
  gender: UserGender;
  isActivated: boolean;
  isBanned: boolean;
}

interface ICreateState {
  nick: string;
  email: string;
  password: string;
  role: UserRole;
  gender: UserGender;
  isActivated: boolean;
}

const ROLE_OPTIONS: { value: RoleFilter; label: string }[] = [
  { value: 'all', label: 'Wszystkie role' },
  { value: 'administrator', label: 'Administratorzy' },
  { value: 'moderator', label: 'Moderatorzy' },
  { value: 'user', label: 'Użytkownicy' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Wszystkie statusy' },
  { value: 'activated', label: 'Zarejestrowani (aktywowani)' },
  { value: 'notActivated', label: 'Niezarejestrowani (nieaktywni)' },
  { value: 'banned', label: 'Zablokowane logowanie' },
];

const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'Wszystkie typy' },
  { value: 'registered', label: 'Zarejestrowani (w bazie)' },
  { value: 'guest', label: 'Goście (Gość_…)' },
  { value: 'ownNick', label: 'Własny nick (niezarejestrowani)' },
];

const DEFAULT_FILTERS: IFilters = {
  role: 'all',
  status: 'all',
  source: 'all',
  search: '',
};

function roleLabel(role: UserRole): string {
  if (role === 'administrator') return 'Administrator';
  if (role === 'moderator') return 'Moderator';
  return 'Użytkownik';
}

function genderLabel(gender: UserGender): string {
  return gender === 'female' ? 'Kobieta' : 'Mężczyzna';
}

function sourceLabel(source: ItemSource): string {
  if (source === 'guest') return 'Gość';
  if (source === 'ownNick') return 'Własny nick';
  return 'Zarejestrowany';
}

function getInitial(s: string): string {
  return (s || '?').charAt(0).toUpperCase();
}

const emptyCreate: ICreateState = {
  nick: '',
  email: '',
  password: '',
  role: 'user',
  gender: 'male',
  isActivated: true,
};

function filtersEqual(a: IFilters, b: IFilters): boolean {
  return a.role === b.role && a.status === b.status && a.source === b.source && a.search === b.search;
}

function Users() {
  const { context } = useGlobalContext();
  const navigate = useNavigate();

  const role = context.authUser?.role;
  const isAdmin = role === 'administrator';
  const isModerator = role === 'moderator';
  const canAccess = isAdmin || isModerator;
  const apiBase = isAdmin ? '/admin' : '/moderator';
  const backRoute = isAdmin ? '/admin' : '/moderator';

  const [dbUsers, setDbUsers] = useState<IDbUser[]>([]);
  const [onlineSessions, setOnlineSessions] = useState<IOnlineSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtry — pending (formularz) vs applied (zastosowane do listy)
  const [pendingFilters, setPendingFilters] = useState<IFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<IFilters>(DEFAULT_FILTERS);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<IEditState | null>(null);
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  const [rowSuccess, setRowSuccess] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createState, setCreateState] = useState<ICreateState>(emptyCreate);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [kickingId, setKickingId] = useState<string | null>(null);
  const [banningId, setBanningId] = useState<string | null>(null);
  const [warningId, setWarningId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [statsCollapsed, setStatsCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('admin_users_statsCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('admin_users_filtersCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleStats = useCallback(() => {
    setStatsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('admin_users_statsCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, []);

  const toggleFilters = useCallback(() => {
    setFiltersCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('admin_users_filtersCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  }, []);

  const activeFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (appliedFilters.role !== 'all') {
      const opt = ROLE_OPTIONS.find(o => o.value === appliedFilters.role);
      if (opt) parts.push(opt.label);
    }
    if (appliedFilters.status !== 'all') {
      const opt = STATUS_OPTIONS.find(o => o.value === appliedFilters.status);
      if (opt) parts.push(opt.label);
    }
    if (appliedFilters.source !== 'all') {
      const opt = SOURCE_OPTIONS.find(o => o.value === appliedFilters.source);
      if (opt) parts.push(opt.label);
    }
    if (appliedFilters.search.trim()) {
      parts.push(`„${appliedFilters.search.trim()}"`);
    }
    return parts.length === 0 ? 'Brak aktywnych filtrów' : parts.join(' · ');
  }, [appliedFilters]);

  useEffect(() => () => {
    if (successTimer.current) clearTimeout(successTimer.current);
  }, []);

  const flashSuccess = useCallback((msg: string) => {
    setRowSuccess(msg);
    if (successTimer.current) clearTimeout(successTimer.current);
    successTimer.current = setTimeout(() => setRowSuccess(null), 3500);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [users, sessions] = await Promise.all([
        apiService(`${apiBase}/users`),
        apiService(`${apiBase}/online-sessions`).catch(() => []),
      ]);
      setDbUsers(Array.isArray(users) ? users : []);
      setOnlineSessions(Array.isArray(sessions) ? sessions : []);
    } catch (e: any) {
      setError(e?.message || 'Nie udało się pobrać listy użytkowników.');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (canAccess) {
      loadAll();
    }
  }, [canAccess, loadAll]);

  // Połącz dane z bazy + sesje online w jedną listę
  const items: IListItem[] = useMemo(() => {
    // Set nicków online (lower-case) → mapowanie nick→sesja(e) niezarejestrowane
    const onlineRegisteredNicks = new Set(
      onlineSessions.filter(s => s.isRegistered).map(s => s.nick.toLowerCase())
    );

    const dbItems: IListItem[] = dbUsers.map(u => ({
      key: `db:${u.id}`,
      source: 'registered',
      id: u.id,
      nick: u.nick,
      email: u.email,
      role: u.role,
      gender: u.gender,
      isBanned: u.isBanned,
      isActivated: u.isActivated,
      isOnline: onlineRegisteredNicks.has(u.nick.toLowerCase()),
      dbUser: u,
    }));

    const ephemeralItems: IListItem[] = onlineSessions
      .filter(s => !s.isRegistered)
      .map(s => ({
        key: `redis:${s.id}`,
        source: s.source === 'guest' ? 'guest' : 'ownNick',
        id: s.id,
        nick: s.nick,
        role: s.role,
        gender: s.gender,
        isOnline: true,
        ipAddress: s.ipAddress,
        deviceType: s.deviceType,
        status: s.status,
      }));

    return [...dbItems, ...ephemeralItems];
  }, [dbUsers, onlineSessions]);

  const filtered = useMemo(() => {
    const q = appliedFilters.search.trim().toLowerCase();
    return items.filter(it => {
      if (appliedFilters.role !== 'all' && it.role !== appliedFilters.role) return false;

      if (appliedFilters.source !== 'all' && it.source !== appliedFilters.source) return false;

      // Status filter dotyczy tylko zarejestrowanych (mają isActivated/isBanned)
      if (appliedFilters.status !== 'all') {
        if (it.source !== 'registered') return false;
        if (appliedFilters.status === 'activated' && !it.isActivated) return false;
        if (appliedFilters.status === 'notActivated' && it.isActivated) return false;
        if (appliedFilters.status === 'banned' && !it.isBanned) return false;
      }

      if (q) {
        const inNick = it.nick.toLowerCase().includes(q);
        const inEmail = !!it.email && it.email.toLowerCase().includes(q);
        if (!inNick && !inEmail) return false;
      }
      return true;
    });
  }, [items, appliedFilters]);

  const stats = useMemo(() => ({
    total: dbUsers.length,
    administrators: dbUsers.filter(u => u.role === 'administrator').length,
    moderators: dbUsers.filter(u => u.role === 'moderator').length,
    activated: dbUsers.filter(u => u.isActivated).length,
    notActivated: dbUsers.filter(u => !u.isActivated).length,
    banned: dbUsers.filter(u => u.isBanned).length,
    onlineGuests: onlineSessions.filter(s => s.source === 'guest').length,
    onlineOwnNick: onlineSessions.filter(s => s.source === 'ownNick' && !s.isRegistered).length,
  }), [dbUsers, onlineSessions]);

  const filtersDirty = !filtersEqual(pendingFilters, appliedFilters);
  const filtersActive = !filtersEqual(appliedFilters, DEFAULT_FILTERS);

  const applyFilters = () => setAppliedFilters(pendingFilters);

  const resetFilters = () => {
    setPendingFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const startEdit = (item: IListItem) => {
    if (item.source !== 'registered' || !item.dbUser) return;
    const u = item.dbUser;
    setEditingId(u.id);
    setEditState({
      nick: u.nick,
      email: u.email,
      password: '',
      role: u.role,
      gender: u.gender,
      isActivated: u.isActivated,
      isBanned: u.isBanned,
    });
    setRowError(null);
    setRowSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditState(null);
    setRowError(null);
  };

  const saveEdit = async (userId: string) => {
    if (!editState) return;
    setSaving(true);
    setRowError(null);
    try {
      const payload: Record<string, any> = {
        nick: editState.nick,
        email: editState.email,
        role: editState.role,
        gender: editState.gender,
        isActivated: editState.isActivated,
        isBanned: editState.isBanned,
      };
      if (editState.password.trim() !== '') payload.password = editState.password;

      const updated: IDbUser = await apiService(`/admin/users/${userId}`, 'PUT', payload);
      setDbUsers(prev => prev.map(u => (u.id === userId ? { ...u, ...updated } : u)));
      flashSuccess(`Zapisano zmiany dla "${updated?.nick ?? editState.nick}".`);
      setEditingId(null);
      setEditState(null);
    } catch (e: any) {
      setRowError(e?.message || 'Nie udało się zapisać zmian.');
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setCreateState(emptyCreate);
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const kickSession = async (item: IListItem) => {
    if (!window.confirm(`Wylogować użytkownika "${item.nick}" z czatu?`)) return;
    let sessionId = item.id;
    if (item.source === 'registered') {
      const session = onlineSessions.find(
        s => s.nick.toLowerCase() === item.nick.toLowerCase()
      );
      if (!session) {
        setRowError('Nie znaleziono aktywnej sesji tego użytkownika.');
        return;
      }
      sessionId = session.id;
    }
    setKickingId(item.id);
    setRowError(null);
    try {
      await apiService(`${apiBase}/users/kick/${sessionId}`, 'POST');
      setOnlineSessions(prev => prev.filter(s => s.id !== sessionId));
      flashSuccess(`Wylogowano użytkownika "${item.nick}" z czatu.`);
    } catch (e: any) {
      setRowError(e?.message || 'Nie udało się wylogować użytkownika.');
    } finally {
      setKickingId(null);
    }
  };

  const warnUser = async (item: IListItem) => {
    if (!item.isOnline) {
      setRowError('Ostrzeżenie można wysłać tylko użytkownikowi online.');
      return;
    }
    if (!window.confirm(`Wysłać ostrzeżenie o banie do "${item.nick}"?`)) return;
    setWarningId(item.id);
    setRowError(null);
    try {
      if (item.source === 'registered') {
        await apiService(`/moderator/users/warn/${item.id}`, 'POST');
      } else {
        await apiService(`/moderator/sessions/warn/${item.id}`, 'POST');
      }
      flashSuccess(`Wysłano ostrzeżenie do "${item.nick}".`);
    } catch (e: any) {
      setRowError(e?.message || 'Nie udało się wysłać ostrzeżenia.');
    } finally {
      setWarningId(null);
    }
  };

  const toggleBan = async (item: IListItem) => {
    if (item.source !== 'registered') return;
    const willBan = !item.isBanned;
    const action = willBan ? 'ban' : 'unban';
    const verb = willBan ? 'zablokować logowanie' : 'odblokować logowanie';
    if (!window.confirm(`Czy na pewno ${verb} użytkownika "${item.nick}"?`)) return;
    setBanningId(item.id);
    setRowError(null);
    try {
      const updated: IDbUser = await apiService(`/moderator/users/${action}/${item.id}`, 'POST');
      setDbUsers(prev => prev.map(u => (u.id === item.id ? { ...u, ...updated } : u)));
      flashSuccess(willBan ? `Zablokowano logowanie dla "${item.nick}".` : `Odblokowano logowanie dla "${item.nick}".`);
    } catch (e: any) {
      setRowError(e?.message || `Nie udało się ${verb} użytkownika.`);
    } finally {
      setBanningId(null);
    }
  };

  const deleteUser = async (item: IListItem) => {
    if (!isAdmin || item.source !== 'registered') return;
    if (!window.confirm(`Usunąć użytkownika "${item.nick}" z bazy? Tej operacji nie można cofnąć.`)) return;
    setDeletingId(item.id);
    setRowError(null);
    try {
      await apiService(`/admin/users/${item.id}`, 'DELETE');
      setDbUsers(prev => prev.filter(u => u.id !== item.id));
      setOnlineSessions(prev => prev.filter(s => s.id !== item.id));
      flashSuccess(`Usunięto użytkownika "${item.nick}".`);
    } catch (e: any) {
      setRowError(e?.message || 'Nie udało się usunąć użytkownika.');
    } finally {
      setDeletingId(null);
    }
  };

  const duplicateUser = async (item: IListItem) => {
    if (!isAdmin || item.source !== 'registered') return;
    setDuplicatingId(item.id);
    setRowError(null);
    try {
      const created: IDbUser = await apiService(`/admin/users/${item.id}/duplicate`, 'POST');
      setDbUsers(prev => [created, ...prev]);
      flashSuccess(`Skopiowano użytkownika jako "${created.nick}".`);
    } catch (e: any) {
      setRowError(e?.message || 'Nie udało się skopiować użytkownika.');
    } finally {
      setDuplicatingId(null);
    }
  };

  const submitCreate = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const created: IDbUser = await apiService('/admin/users', 'POST', createState);
      setDbUsers(prev => [created, ...prev]);
      flashSuccess(`Utworzono użytkownika "${created.nick}".`);
      setCreateOpen(false);
      setCreateState(emptyCreate);
    } catch (e: any) {
      setCreateError(e?.message || 'Nie udało się utworzyć użytkownika.');
    } finally {
      setCreating(false);
    }
  };

  if (!canAccess) {
    return (
      <div className={adminStyles.accessDenied}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <h2>Brak dostępu</h2>
        <p>Ta strona jest dostępna tylko dla moderatorów i administratorów.</p>
        <button className={adminStyles.refreshBtn} onClick={() => navigate('/')}>Wróć na stronę główną</button>
      </div>
    );
  }

  return (
    <div className={adminStyles.pageWrapper}>
      <div className={adminStyles.topBar}>
        <button
          className={adminStyles.backBtn}
          onClick={() => navigate(backRoute)}
          title={isAdmin ? 'Wróć do panelu admina' : 'Wróć do panelu moderatora'}
        >
          ←
        </button>
        <span className={adminStyles.topBarTitle}>Użytkownicy</span>
        <button
          className={adminStyles.refreshBtn}
          onClick={loadAll}
          disabled={loading}
          title="Odśwież listę"
        >
          {loading ? <span className={adminStyles.spinning}>↻</span> : '↻'} Odśwież
        </button>
        {isAdmin && (
          <button className={styles.primaryBtn} onClick={openCreate}>
            + Nowy użytkownik
          </button>
        )}
      </div>

      <div className={styles.statsSection}>
        <button
          type="button"
          className={styles.statsToggle}
          onClick={toggleStats}
          aria-expanded={!statsCollapsed}
          title={statsCollapsed ? 'Rozwiń statystyki' : 'Zwiń statystyki'}
        >
          <span className={`${styles.statsToggleArrow} ${statsCollapsed ? styles.statsToggleArrowCollapsed : ''}`}>▾</span>
          <span className={styles.statsToggleLabel}>Statystyki</span>
          {statsCollapsed && (
            <span className={styles.statsToggleSummary}>
              W bazie: <strong>{stats.total}</strong> · Online niezarej.: <strong>{stats.onlineGuests + stats.onlineOwnNick}</strong> · Zablokowane logowanie: <strong>{stats.banned}</strong>
            </span>
          )}
        </button>
        {!statsCollapsed && (
          <div className={styles.statsBar}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>Wszyscy w bazie</span>
              <span className={styles.statValue}>{stats.total}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statAdmin}`}>
              <span className={styles.statLabel}>Administratorzy</span>
              <span className={styles.statValue}>{stats.administrators}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statMod}`}>
              <span className={styles.statLabel}>Moderatorzy</span>
              <span className={styles.statValue}>{stats.moderators}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statOk}`}>
              <span className={styles.statLabel}>Aktywowani</span>
              <span className={styles.statValue}>{stats.activated}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statWarn}`}>
              <span className={styles.statLabel}>Nieaktywni</span>
              <span className={styles.statValue}>{stats.notActivated}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statBan}`}>
              <span className={styles.statLabel}>Blokada logowania</span>
              <span className={styles.statValue}>{stats.banned}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statGuest}`}>
              <span className={styles.statLabel}>Goście online</span>
              <span className={styles.statValue}>{stats.onlineGuests}</span>
            </div>
            <div className={`${styles.statCard} ${styles.statOwnNick}`}>
              <span className={styles.statLabel}>Własny nick online</span>
              <span className={styles.statValue}>{stats.onlineOwnNick}</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.filtersSection}>
        <button
          type="button"
          className={styles.statsToggle}
          onClick={toggleFilters}
          aria-expanded={!filtersCollapsed}
          title={filtersCollapsed ? 'Rozwiń filtry' : 'Zwiń filtry'}
        >
          <span className={`${styles.statsToggleArrow} ${filtersCollapsed ? styles.statsToggleArrowCollapsed : ''}`}>▾</span>
          <span className={styles.statsToggleLabel}>Filtry</span>
          {filtersCollapsed && (
            <span className={styles.statsToggleSummary}>
              {activeFilterSummary}
            </span>
          )}
          {filtersCollapsed && filtersDirty && (
            <span className={styles.dirtyHint}>● Zmiany niezapisane</span>
          )}
        </button>
        {!filtersCollapsed && (
        <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarGroup}>
          <label className={styles.fieldLabel}>
            <span>Rola</span>
            <select
              className={styles.select}
              value={pendingFilters.role}
              onChange={e => setPendingFilters(s => ({ ...s, role: e.target.value as RoleFilter }))}
            >
              {ROLE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.fieldLabel}>
            <span>Status</span>
            <select
              className={styles.select}
              value={pendingFilters.status}
              onChange={e => setPendingFilters(s => ({ ...s, status: e.target.value as StatusFilter }))}
            >
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.fieldLabel}>
            <span>Typ konta</span>
            <select
              className={styles.select}
              value={pendingFilters.source}
              onChange={e => setPendingFilters(s => ({ ...s, source: e.target.value as SourceFilter }))}
            >
              {SOURCE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>
        <label className={`${styles.fieldLabel} ${styles.searchField}`}>
          <span>Szukaj po nicku lub e-mailu</span>
          <div className={styles.searchInputWrapper}>
            <svg className={styles.searchIcon} width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Wpisz nick lub e-mail…"
              value={pendingFilters.search}
              onChange={e => setPendingFilters(s => ({ ...s, search: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters(); }}
            />
            {pendingFilters.search && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => setPendingFilters(s => ({ ...s, search: '' }))}
                aria-label="Wyczyść"
              >×</button>
            )}
          </div>
        </label>
      </div>

      <div className={styles.applyBar}>
        <div className={styles.applyHints}>
          {filtersDirty && (
            <span className={styles.dirtyHint}>● Filtry zmienione — kliknij „Pokaż wyniki"</span>
          )}
          {filtersActive && !filtersDirty && (
            <span className={styles.activeHint}>Aktywne filtry zastosowane</span>
          )}
        </div>
        <div className={styles.applyActions}>
          {filtersActive && (
            <button className={styles.cancelBtn} onClick={resetFilters} disabled={loading}>
              Wyczyść filtry
            </button>
          )}
          <button
            className={`${styles.primaryBtn} ${filtersDirty ? styles.primaryBtnPulse : ''}`}
            onClick={applyFilters}
            disabled={loading}
          >
            Pokaż wyniki
          </button>
        </div>
      </div>
        </>
        )}
      </div>

      {error && <div className={adminStyles.errorBar}>{error}</div>}
      {rowError && <div className={adminStyles.errorBar}>{rowError}</div>}
      {rowSuccess && <div className={styles.successBar}>{rowSuccess}</div>}

      <div className={styles.listWrapper}>
        <div className={styles.listMeta}>
          Wyświetlono <strong>{filtered.length}</strong> z <strong>{items.length}</strong>
          {' '}użytkowników (w bazie: <strong>{dbUsers.length}</strong>, online niezarejestrowanych:{' '}
          <strong>{stats.onlineGuests + stats.onlineOwnNick}</strong>)
        </div>

        {loading ? (
          <div className={styles.empty}>Ładowanie…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <p>Brak użytkowników spełniających kryteria.</p>
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {filtered.map(it => {
              const isEditing = it.source === 'registered' && editingId === it.id;
              const isSelf = it.source === 'registered' && it.id === context.authUser?.id;
              const canEdit = it.source === 'registered';

              return (
                <div
                  key={it.key}
                  className={`${styles.userCard} ${isEditing ? styles.userCardEditing : ''} ${isSelf ? styles.userCardSelf : ''} ${!canEdit ? styles.userCardEphemeral : ''}`}
                >
                  <div className={styles.cardHeader}>
                    <div className={`${styles.avatar} ${styles[`avatar_${it.role}`]} ${!canEdit ? styles[`avatar_${it.source}`] : ''}`}>
                      {getInitial(it.nick)}
                    </div>
                    <div className={styles.headerText}>
                      <div className={styles.headerNick}>
                        {it.nick}
                        {isSelf && <span className={styles.selfBadge}>Ty</span>}
                        {it.isOnline && <span className={styles.onlineDot} title="Online" />}
                      </div>
                      <div className={styles.headerEmail}>
                        {it.email
                          ? it.email
                          : it.ipAddress
                            ? `IP: ${it.ipAddress}`
                            : '— sesja tymczasowa —'}
                      </div>
                    </div>
                    <div className={styles.headerBadges}>
                      <span className={`${styles.sourceBadge} ${styles[`source_${it.source}`]}`}>
                        {sourceLabel(it.source)}
                      </span>
                      <span className={`${styles.roleBadge} ${styles[`role_${it.role}`]}`}>
                        {roleLabel(it.role)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.cardBody}>
                    {isEditing ? (
                      <div className={styles.editForm}>
                        <label className={styles.editField}>
                          <span>Nick</span>
                          <input
                            type="text"
                            className={styles.input}
                            value={editState!.nick}
                            onChange={e => setEditState(s => s && { ...s, nick: e.target.value })}
                          />
                        </label>
                        <label className={styles.editField}>
                          <span>E-mail</span>
                          <input
                            type="email"
                            className={styles.input}
                            value={editState!.email}
                            onChange={e => setEditState(s => s && { ...s, email: e.target.value })}
                            autoComplete="off"
                          />
                        </label>
                        <label className={styles.editField}>
                          <span>Hasło (puste = bez zmian)</span>
                          <input
                            type="text"
                            className={styles.input}
                            placeholder="Nowe hasło"
                            value={editState!.password}
                            onChange={e => setEditState(s => s && { ...s, password: e.target.value })}
                            autoComplete="new-password"
                          />
                        </label>
                        <label className={styles.editField}>
                          <span>Rola</span>
                          <select
                            className={styles.input}
                            value={editState!.role}
                            onChange={e => setEditState(s => s && { ...s, role: e.target.value as UserRole })}
                          >
                            <option value="user">Użytkownik</option>
                            <option value="moderator">Moderator</option>
                            <option value="administrator">Administrator</option>
                          </select>
                        </label>
                        <label className={styles.editField}>
                          <span>Płeć</span>
                          <select
                            className={styles.input}
                            value={editState!.gender}
                            onChange={e => setEditState(s => s && { ...s, gender: e.target.value as UserGender })}
                          >
                            <option value="male">Mężczyzna</option>
                            <option value="female">Kobieta</option>
                          </select>
                        </label>
                        <div className={styles.toggleRow}>
                          <label className={styles.toggle}>
                            <input
                              type="checkbox"
                              checked={editState!.isActivated}
                              onChange={e => setEditState(s => s && { ...s, isActivated: e.target.checked })}
                            />
                            <span className={styles.toggleSwitch} />
                            <span className={styles.toggleLabel}>Konto aktywne</span>
                          </label>
                          <label className={styles.toggle}>
                            <input
                              type="checkbox"
                              checked={editState!.isBanned}
                              onChange={e => setEditState(s => s && { ...s, isBanned: e.target.checked })}
                            />
                            <span className={`${styles.toggleSwitch} ${styles.toggleSwitchDanger}`} />
                            <span className={styles.toggleLabel}>Zablokowane logowanie</span>
                          </label>
                        </div>
                      </div>
                    ) : canEdit ? (
                      <div className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Płeć</span>
                          <span className={styles.infoValue}>{genderLabel(it.gender)}</span>
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Status</span>
                          <span
                            className={`${styles.statusBadge} ${
                              it.isActivated ? styles.statusActivated : styles.statusNotActivated
                            }`}
                          >
                            {it.isActivated ? '● Aktywne' : '○ Nieaktywne'}
                          </span>
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Blokada logowania</span>
                          {it.isBanned ? (
                            <span className={styles.bannedBadge}>🚫 Zablokowane</span>
                          ) : (
                            <span className={styles.infoValueMuted}>—</span>
                          )}
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Hasło</span>
                          <span className={styles.passwordPlaceholder}>••••••••</span>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.infoGrid}>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Płeć</span>
                          <span className={styles.infoValue}>{genderLabel(it.gender)}</span>
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Urządzenie</span>
                          <span className={styles.infoValue}>{it.deviceType || '—'}</span>
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>IP</span>
                          <span className={styles.infoValue}>{it.ipAddress || '—'}</span>
                        </div>
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Sesja</span>
                          <span className={styles.statusBadge + ' ' + styles.statusActivated}>
                            ● {it.status === 'inactive' ? 'Nieaktywna' : 'Aktywna'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={styles.cardFooter}>
                    {isEditing ? (
                      <>
                        <button
                          className={styles.cancelBtn}
                          onClick={cancelEdit}
                          disabled={saving}
                        >
                          Anuluj
                        </button>
                        <button
                          className={styles.saveBtn}
                          onClick={() => saveEdit(it.id)}
                          disabled={saving}
                        >
                          {saving ? 'Zapisywanie…' : 'Zapisz'}
                        </button>
                      </>
                    ) : canEdit ? (
                      <>
                        {it.isOnline && !isSelf && (
                          <button
                            className={styles.editBtn}
                            onClick={() => warnUser(it)}
                            disabled={warningId === it.id}
                            title="Wyślij ostrzeżenie o banie"
                          >
                            {warningId === it.id ? 'Wysyłanie…' : '⚠️ Ostrzeż'}
                          </button>
                        )}
                        {it.isOnline && !isSelf && (
                          <button
                            className={styles.kickBtn}
                            onClick={() => kickSession(it)}
                            disabled={kickingId === it.id}
                            title="Wyloguj użytkownika z czatu"
                          >
                            {kickingId === it.id ? 'Wylogowywanie…' : '🚪 Wyloguj'}
                          </button>
                        )}
                        {!isSelf && (
                          <button
                            className={styles.kickBtn}
                            onClick={() => toggleBan(it)}
                            disabled={banningId === it.id}
                            title={it.isBanned ? 'Odblokuj możliwość logowania użytkownika' : 'Zablokuj możliwość logowania użytkownika'}
                          >
                            {banningId === it.id
                              ? (it.isBanned ? 'Odblokowywanie…' : 'Blokowanie…')
                              : (it.isBanned ? '✓ Odblokuj logowanie' : '🚫 Zablokuj logowanie')}
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button
                              className={styles.editBtn}
                              onClick={() => duplicateUser(it)}
                              disabled={duplicatingId === it.id}
                              title="Utwórz kopię użytkownika"
                            >
                              {duplicatingId === it.id ? 'Kopiowanie…' : '⧉ Kopiuj'}
                            </button>
                            <button className={styles.editBtn} onClick={() => startEdit(it)}>
                              ✎ Edytuj
                            </button>
                            {!isSelf && (
                              <button
                                className={styles.kickBtn}
                                onClick={() => deleteUser(it)}
                                disabled={deletingId === it.id}
                                title="Usuń z bazy danych"
                              >
                                {deletingId === it.id ? 'Usuwanie…' : '🗑 Usuń'}
                              </button>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <span className={styles.ephemeralNote}>
                          Sesja tymczasowa — brak rekordu w bazie
                        </span>
                        <button
                          className={styles.editBtn}
                          onClick={() => warnUser(it)}
                          disabled={warningId === it.id}
                          title="Wyślij ostrzeżenie o banie"
                        >
                          {warningId === it.id ? 'Wysyłanie…' : '⚠️ Ostrzeż'}
                        </button>
                        <button
                          className={styles.kickBtn}
                          onClick={() => kickSession(it)}
                          disabled={kickingId === it.id}
                          title="Wyloguj użytkownika z czatu"
                        >
                          {kickingId === it.id ? 'Wylogowywanie…' : '🚪 Wyloguj'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {createOpen && (
        <div className={styles.modalBackdrop} onClick={closeCreate}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Nowy użytkownik</h2>
              <button className={styles.modalClose} onClick={closeCreate} aria-label="Zamknij">×</button>
            </div>
            <div className={styles.modalBody}>
              <label className={styles.editField}>
                <span>Nick *</span>
                <input
                  type="text"
                  className={styles.input}
                  value={createState.nick}
                  onChange={e => setCreateState(s => ({ ...s, nick: e.target.value }))}
                  autoFocus
                />
              </label>
              <label className={styles.editField}>
                <span>E-mail *</span>
                <input
                  type="email"
                  className={styles.input}
                  value={createState.email}
                  onChange={e => setCreateState(s => ({ ...s, email: e.target.value }))}
                />
              </label>
              <label className={styles.editField}>
                <span>Hasło * (min. 6 znaków)</span>
                <input
                  type="text"
                  className={styles.input}
                  value={createState.password}
                  onChange={e => setCreateState(s => ({ ...s, password: e.target.value }))}
                  autoComplete="new-password"
                />
              </label>
              <div className={styles.modalRow}>
                <label className={styles.editField}>
                  <span>Rola</span>
                  <select
                    className={styles.input}
                    value={createState.role}
                    onChange={e => setCreateState(s => ({ ...s, role: e.target.value as UserRole }))}
                  >
                    <option value="user">Użytkownik</option>
                    <option value="moderator">Moderator</option>
                    <option value="administrator">Administrator</option>
                  </select>
                </label>
                <label className={styles.editField}>
                  <span>Płeć</span>
                  <select
                    className={styles.input}
                    value={createState.gender}
                    onChange={e => setCreateState(s => ({ ...s, gender: e.target.value as UserGender }))}
                  >
                    <option value="male">Mężczyzna</option>
                    <option value="female">Kobieta</option>
                  </select>
                </label>
              </div>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={createState.isActivated}
                  onChange={e => setCreateState(s => ({ ...s, isActivated: e.target.checked }))}
                />
                <span className={styles.toggleSwitch} />
                <span className={styles.toggleLabel}>
                  Konto aktywne (gotowe do logowania)
                </span>
              </label>

              {createError && <div className={styles.modalError}>{createError}</div>}
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.cancelBtn}
                onClick={closeCreate}
                disabled={creating}
              >
                Anuluj
              </button>
              <button
                className={styles.saveBtn}
                onClick={submitCreate}
                disabled={creating || !createState.nick || !createState.email || !createState.password}
              >
                {creating ? 'Tworzenie…' : 'Utwórz użytkownika'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
